import React, { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { QrCode, CalendarCheck, TrendingUp, Printer } from "lucide-react";
import api from "../../lib/api";

export function CheckInTab({ user }) {
  const [data, setData] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const isAdmin = user?.role === "admin";
  const qrUrl = qrCode ? `${window.location.origin}/checkin?code=${encodeURIComponent(qrCode)}` : "";

  useEffect(() => {
    api.get("/member/checkins").then((r) => setData(r.data)).catch(() => {});
    if (isAdmin) api.get("/member/checkin-code").then((r) => setQrCode(r.data.code)).catch(() => {});
  }, [isAdmin]);

  return (
    <div className="space-y-6" data-testid="checkin-tab">
      {/* Member status */}
      <div className="grid md:grid-cols-3 gap-5">
        <div className="iri-gradient rounded-2xl p-7" data-testid="checkin-status-card">
          <CalendarCheck className="h-6 w-6 text-ink/70" strokeWidth={1.5} />
          <p className="text-ink/70 text-sm mt-4">Today's check-in</p>
          <p className="font-heading text-2xl font-light text-ink mt-1">
            {data?.checked_in_today ? "Checked in ✓" : "Not yet today"}
          </p>
        </div>
        <div className="bg-white rounded-2xl p-7 border border-slate-100">
          <TrendingUp className="h-6 w-6 text-iri-mid" strokeWidth={1.5} />
          <p className="text-[#8A9492] text-sm mt-4">Total visits</p>
          <p className="font-heading text-5xl font-light text-ink mt-1" data-testid="checkin-total-visits">
            {data?.total_visits ?? 0}
          </p>
        </div>
        <div className="bg-white rounded-2xl p-7 border border-slate-100">
          <QrCode className="h-6 w-6 text-iri-mid" strokeWidth={1.5} />
          <p className="text-[#8A9492] text-sm mt-4">Points per visit</p>
          <p className="font-heading text-5xl font-light text-ink mt-1">{data?.points_per_visit ?? 50}</p>
        </div>
      </div>

      {/* Admin QR poster */}
      {isAdmin && qrUrl && (
        <div className="bg-white rounded-2xl p-7 border border-slate-100 text-center" data-testid="admin-qr-poster">
          <h2 className="font-heading text-xl font-medium text-ink">Reception check-in QR</h2>
          <p className="text-sm text-[#5C6664] mt-1 max-w-md mx-auto">
            Display this on your front-desk tablet. Members scan it with their phone to earn {data?.points_per_visit ?? 50} points per visit (once daily).
          </p>
          <div className="inline-flex flex-col items-center bg-stone rounded-2xl p-8 mt-6" id="qr-print-area">
            <QRCodeCanvas value={qrUrl} size={240} level="M" includeMargin fgColor="#1A1A1A" data-testid="checkin-qr-canvas" />
            <p className="mt-4 font-heading text-lg text-ink">Scan to check in</p>
            <p className="text-xs text-[#8A9492]">Iridescent Wellness Spa</p>
          </div>
          <div className="mt-6">
            <button onClick={() => window.print()} data-testid="print-qr-btn"
              className="inline-flex items-center gap-2 text-sm text-ink border border-ink/20 rounded-full px-5 py-2 hover:bg-stone transition-colors">
              <Printer className="h-4 w-4" /> Print poster
            </button>
          </div>
        </div>
      )}

      {/* How it works (members) */}
      {!isAdmin && (
        <div className="bg-white rounded-2xl p-7 border border-slate-100" data-testid="checkin-howto">
          <h2 className="font-heading text-xl font-medium text-ink mb-3">Earn points every visit</h2>
          <ol className="space-y-2 text-sm text-[#5C6664] list-decimal list-inside">
            <li>When you arrive, scan the QR code at our reception desk with your phone camera.</li>
            <li>You'll be checked in automatically and earn {data?.points_per_visit ?? 50} reward points.</li>
            <li>Check in once per visit — points post instantly to your balance.</li>
          </ol>
        </div>
      )}

      {/* History */}
      <div className="bg-white rounded-2xl p-7 border border-slate-100" data-testid="checkin-history">
        <h2 className="font-heading text-xl font-medium text-ink mb-4">Check-in history</h2>
        {!data || data.checkins.length === 0 ? (
          <p className="text-sm text-[#8A9492]">No check-ins yet. Scan the reception QR on your next visit to start earning!</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.checkins.slice().reverse().map((c) => (
              <li key={c.id} className="py-4 flex items-center justify-between" data-testid={`checkin-item-${c.id}`}>
                <p className="text-ink font-medium">{new Date(c.at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</p>
                <span className="text-sm text-iri-mid font-medium">+{c.points} pts</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
